/**
 * E, in place of rows 2–5 (spec §5, R7). noSpend: "<channel> ไม่มี spend ในช่วงที่เลือก
 * · วิ่งครั้งล่าสุด <date> · ลองขยายช่วงเป็น 28 วัน". The date is the page's short date
 * with its year (fmtShortDate, R27 / R36: "12 ต.ค. 2026"), left out when it is
 * unknown (the entities are loading or failed) or the channel never spent. The
 * 28-day hint shows only while the window is shorter than 28 days: on a longer
 * one, "widen to 28 days" would narrow it. D1 and D4 can be filtered to several channels or to none
 * (channelLabel null): then the headline names none, "ไม่มี spend ในช่วงที่เลือก", and the page's channel chip names
 * the ticked ones.
 * notConnected: "<channel> ยังไม่ได้เชื่อมต่อ", nothing more; on D1 and D4 too, for the one ticked channel known not
 * to be connected (rules-04 G3). The headline is emptyHeadline, by which the files and the chat say it too.
 *
 * @module components/channels/ChannelEmptyState
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { emptyHeadline, fmtShortDate } from '@/services/channels/channelFormat';
import type { ChannelEmpty } from '@/services/channels/channelTypes';

/** The window channels.empty.tryLonger proposes. */
const TRY_LONGER_DAYS = 28;

interface Props {
  empty: ChannelEmpty;
  /** The channel the headline names; null = every channel in view (noSpend only). */
  channelLabel: string | null;
  /** The current window's length in days: rangeDays(windows.current). */
  windowDays: number;
}

/** The sentence's parts, the headline first. Literal keys, so the locale integrity test sees them. */
const partsOf = ({ empty, channelLabel: channel, windowDays }: Props, t: TFunction): string[] => {
  const head = emptyHeadline(empty.kind, channel, t);
  if (empty.kind === 'notConnected') return [head];
  return [
    head,
    ...(empty.lastSpendDate ? [t('channels.empty.lastRun', { date: fmtShortDate(empty.lastSpendDate, t) })] : []),
    ...(windowDays < TRY_LONGER_DAYS ? [t('channels.empty.tryLonger')] : []),
  ];
};

export const ChannelEmptyState: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [head, ...rest] = partsOf(props, t);
  return (
    <section data-channel-empty={props.empty.kind} className="rounded-2xl border border-neutral-200 bg-white px-4 py-10 text-center shadow-sm">
      <p className="text-sm text-neutral-700">
        <span className="text-base font-semibold text-ink-950">{head}</span>
        {rest.map((part) => ` · ${part}`).join('')}
      </p>
    </section>
  );
};
